import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { PropsAndInputAttrs } from '../../utils/ReactHelpers';
import { TextFieldProps, TextFieldWidget } from '../TextField/TextFieldWidget';
import { ColorSwatchWidget } from './ColorGridWidget';

const { RowHeight, BorderWidth } = TabStyles.Sizing;

interface HexColorInputState {
  hexColorText: string;
}

export type HexColorInputProps = Pick<PropsAndInputAttrs<TextFieldProps>, 'label'> & {
  children?: never;
  /** Added to the root element. */
  className?: string;
  /** The current color, formatted as a #RRGGBB hex code. Should be updated in response to `onConfirm` event. */
  color: string;
  /** Forwarded to the `ref` prop on the inner `TextFieldWidget`. */
  inputRef?: React.Ref<HTMLInputElement>;
  /** Forwarded to the `style` prop on the inner `TextFieldWidget`. */
  inputStyle?: React.CSSProperties;
  /** Invoked when the text field has a valid color value and either the user presses ENTER or the swatch is clicked. */
  onConfirm: (color: string) => void;
  /** Added to the root element with suffix `'-HexColorInput'` */
  testId?: string;
}

const RootStyleName = TypeStyle.style({
  alignItems: 'flex-end',
  display: 'flex',
  paddingTop: 3,
  paddingBottom: 3,
});

const PreviewSwatchStyleName = TypeStyle.style({
  borderColor: TabStyles.Colors.L6,
  borderStyle: 'solid',
  borderWidth: BorderWidth,
  boxSizing: 'border-box',
  height: RowHeight,
  marginRight: 12,
  width: RowHeight,
});

const TextFieldStyleName = TypeStyle.style({
  flexBasis: '50px',
  flexGrow: 1,
  $nest: {
    '& input': { width: '100%' }
  }
});

function isValidHexColor(color: string): boolean {
  return /^#?[0-9A-Fa-f]{6}$/.test(color);
}

function formatColor(color: string): string {
  let formattedColor = (color || '').toUpperCase();
  if (formattedColor.charAt(0) !== '#') {
    formattedColor = '#' + formattedColor;
  }
  return formattedColor;
}

export class HexColorInputWidget extends React.PureComponent<HexColorInputProps, HexColorInputState> {
  public state: HexColorInputState = {
    hexColorText: this.props.color || '',
  };

  public render(): JSX.Element {
    return (
      <div className={TypeStyle.classes(RootStyleName, this.props.className)} data-tb-test-id={makeTestId(this.props.testId, 'HexColorInput')}>
        <div className={PreviewSwatchStyleName}>
          <ColorSwatchWidget
            color={this.getCustomColor() || 'transparent'}
            disabled={!this.getCustomColor()}
            onClick={this.confirmIfValid}
            selected={this.shouldSwatchBeSelected()}
            size={RowHeight - 2 * BorderWidth}
            testId={this.props.testId}
          />
        </div>
        <TextFieldWidget
          className={TextFieldStyleName}
          label={this.props.label}
          maxLength={7}
          onChange={this.handleChange}
          /* onClear is omitted because the TextField from Tableau UI calls stopPropagation on ESC events when there is text present --
          * which is always the case for this control, as it always displays the '#' prefix. Removing this stopPropagation would be
          * technically-correct but would break dialogs/overlays, etc. Easiest way to work around is just not to show the clear button. */
          onKeyDown={this.handleKeyDown}
          placeholder='#'
          ref={this.props.inputRef}
          // TFSID 1165428: Default is 20, which causes the input to grow too wide.
          size={10}
          spellCheck={false}
          style={this.props.inputStyle}
          testId={this.props.testId!}
          valid={this.isValid()}
          value={formatColor(this.state.hexColorText)}
        />
      </div>
    );
  }

  private isValid = (): TextFieldProps['valid'] => {
    if (!this.state.hexColorText || this.state.hexColorText === '#' || isValidHexColor(this.state.hexColorText)) {
      return undefined;
    }
    return false;
  }

  private handleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const { value } = e.target;
    if (!/^#?[0-9A-Fa-f]{0,6}$/.test(value)) { return; }
    this.setState({ hexColorText: value });
  }

  private handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (!e.repeat) {
        this.confirmIfValid();
      }
    }
  }

  private confirmIfValid = () => {
    if (isValidHexColor(this.state.hexColorText)) {
      this.props.onConfirm(formatColor(this.state.hexColorText));
    }
  }

  private getCustomColor(): string | null {
    if (isValidHexColor(this.state.hexColorText)) {
      return formatColor(this.state.hexColorText);
    }
    return null;
  }

  private shouldSwatchBeSelected(): boolean {
    return isValidHexColor(this.props.color) && formatColor(this.state.hexColorText) === formatColor(this.props.color);
  }
}
